import { ErrorCodes } from '../utils/ErrorCodes';
import Logger from '../utils/Logger';
import NawsCommunication from './NawsCommunication';

export default class NawsEngine {
	// stores the name of this class
	static className = 'NawsEngine';

	// stores active Naws session data received from calling Naws/GetSession api
	static session = {};

	// stores valid printers list
	// static printers = this.processPrinters(printers);
	static printers = [];

	// regex search criteria for printers to exclude
	static printerExclude = /(fax)|(webex)|(onenote)|(DYMO LabelWriter 450 DUO Tape)/i;

	/**
	 * Pass the response object from CreateIndicium, etc. to Naws/Print API.
	 */
	static print = (printObj, callback, device) => {
		const printCommandArray = printObj.printCommand ? printObj.printCommand.split(' ') : [];
		const printCommandKeyArray = printObj.printCommandX
			? printObj.printCommandX.split(' ')
			: [];

		this.printRecursive(device, printCommandArray, printCommandKeyArray, 0, callback);
	};

	static printRecursive = async (device, printCommandArray, printCommandKeyArray, index, callback) => {
		let count = index;

		// call API and initiate print
		const response = await NawsCommunication.print({
			sessionId: device.SessionID,
			printCommand: printCommandArray[count],
			printCommandX: printCommandKeyArray[count],
			SDCConnectServerHost: device.SdcConnectServerHost,
		});

		if (NawsCommunication.isSuccessful(response)) {
			// eslint-disable-next-line no-plusplus
			count++;
			if (response.Success === true) {
				if (count < printCommandArray.length) {
					this.printRecursive(
						device,
						printCommandArray,
						printCommandKeyArray,
						count,
						callback
					);
				} else {
					response.errorCode = ErrorCodes.SUCCESS;
					callback(response);
				}
			} else {
				callback(response);
			}
		} else {
			callback(response);
		}
	};

	/**
	 * Make a call to Naws/GetWeight api to retrieve weight information and pass the response object to the caller via the callback.
	 */
	static getWeight = async (callback, sessionId) => {
		const response = await NawsCommunication.getWeight({
			sessionId,
		});

		if (NawsCommunication.isSuccessful(response)) {
			const { data = {} } = response;

			if (data.weight < 0) {
				response.errorCode = data.weight;
			} else {
				response.errorCode = ErrorCodes.SUCCESS;
			}

			callback(response);
		} else {
			callback(response);
		}
	};

	/**
	 * Make a call to Naws/ResetScale api to reset the scale.
	 */
	static resetScale = async (callback, sessionId) => {
		const response = await NawsCommunication.resetScale({
			sessionId,
		});

		if (NawsCommunication.isSuccessful(response)) {
			const { data = {} } = response;

			if (data.weight < 0) {
				response.errorCode = data.weight;
			} else {
				response.errorCode = ErrorCodes.SUCCESS;
			}

			callback(response);
		} else {
			callback(response);
		}
	};

	static getPrinterNamesArray = printerList =>
		printerList.map(printerObj =>
			typeof printerObj === 'object' ? printerObj.name : 'printerObj invalid'
		);

	/**
	 * Make back-to-back calls to Naws/RefreshPrinters api and Naws/GetSessionDetails api to retrieve user’s active Naws session
	 * data where printer list is available. Call processPrinters method to filter out invalid printers then store valid printers
	 * in the local property printers. Execute the callback with valid printers if the callback is passed in.
	 */
	static refreshPrinters = (callback, deviceSessionID, printerInfoTime) => {
		const intervalTime = 5000;
		const maxAttempts = 10;
		let currentAttempts = 0,
			intervalId,
			printerNamesArray,
			nawsSessionInfo,
			nawsSessionPrinterInfo;

		NawsCommunication.refreshPrinters({
			sessionId: deviceSessionID,
			success(refreshPrinterResponse) {
				if (refreshPrinterResponse.Success === true) {
					intervalId = setInterval(() => {
						NawsCommunication.getSessionDetails({
							params: {
								sessionId: deviceSessionID,
							},
							success(response) {
								if (typeof response.nawsSessionInfo === 'object') {
									// convert string to Date Object and save it in response object
									nawsSessionInfo = {
										...response.nawsSessionInfo,
										SessionID: deviceSessionID,
										PrinterInfoTime: new Date(
											response.nawsSessionInfo.PrinterInfoTime
										),
									};
								}

								// Check if session printerInfoTime has been updated, and if so, clearInterval and make callback
								if (
									nawsSessionInfo &&
									nawsSessionInfo.PrinterInfoTime > printerInfoTime
								) {
									clearInterval(intervalId);
									response.errorCode = ErrorCodes.SUCCESS;
									nawsSessionPrinterInfo = nawsSessionInfo.PrinterInfo
										? nawsSessionInfo.PrinterInfo.printer
										: [];
									printerNamesArray =
										nawsSessionPrinterInfo.length > 0
											? this.getPrinterNamesArray(nawsSessionPrinterInfo)
											: [];

									Logger.logToServer(
										{
											category: 'refresh_printer',
											printer_list: printerNamesArray,
										},
										Logger.INFO
									);

									callback(response);
								} else {
									// If not updated, increase attempts. If max attempts reached, clearInterval and make callback with error
									// eslint-disable-next-line no-plusplus
									currentAttempts++;
									if (currentAttempts === maxAttempts) {
										clearInterval(intervalId);
										response.errorCode = ErrorCodes.REFRESH_PRINTERS_FAILED;
										response.errorDescription =
											'PrinterInfoTime not updated after reaching max attempts';

										Logger.logToServer(
											{
												category: 'refresh_printer',
												error: response.errorDescription,
											},
											Logger.INFO
										);

										callback(response);
									}
								}
							},
						});
					}, intervalTime);
				} else {
					callback(refreshPrinterResponse);
				}
			},
			failure(refreshPrinterResponse) {
				callback(refreshPrinterResponse);
			},
		});
	};

	/**
	 * Loop through the printers array and filter out invalid printers.
	 * @return {array}
	 */
	static processPrinters = printers => {
		const filteredPrinters = [];

		let printerName;

		if (printers && Array.isArray(printers)) {
			for (let i = 0; i < printers.length; i++) {
				printerName = printers[i].name;

				if (!this.printerExclude.test(printerName)) {
					filteredPrinters.push(printers[i]);
				}
			}
		}

		return filteredPrinters;
	};
}
